import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  Activity,
  Brain,
  Check,
  Gauge,
  History,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Webhook,
  Wrench,
} from "lucide-react"
import * as React from "react"
import { type Resolver, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { ChatEndpoint } from "@/api/generated/ChatEndpoint"
import { useAgents } from "@/api/queries/agents"
import {
  endpointUrl,
  useChatEndpoints,
  useCreateChatEndpoint,
  useDeleteChatEndpoint,
  useRotateChatEndpointToken,
  useUpdateChatEndpoint,
} from "@/api/queries/chat-endpoints"
import { CodeBlock } from "@/components/app/code-block"
import { useConfirm } from "@/components/app/confirm-dialog"
import { CopyButton } from "@/components/app/copy-button"
import { createColumns, DataTable } from "@/components/app/data-table"
import { NumberField, SelectField, SwitchField, TextField } from "@/components/app/fields"
import { FormSheet } from "@/components/app/form"
import { KeyValueList } from "@/components/app/key-value"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { SecretReveal } from "@/components/app/secret-reveal"
import { StatCard } from "@/components/app/stat-card"
import { EmptyState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { endpointCurl } from "@/features/chat/templates"
import i18n from "@/i18n"
import { formatNumber, toDate } from "@/lib/format"
import { z } from "@/lib/zod"

interface EndpointsSearch {
  edit?: number | "new"
}

export const Route = createFileRoute("/_app/studio/endpoints")({
  staticData: { titleKey: "nav.items.endpoints" },
  validateSearch: (s: Record<string, unknown>): EndpointsSearch => {
    if (s.edit === "new") return { edit: "new" }
    const id = Number(s.edit)
    return Number.isInteger(id) && id > 0 ? { edit: id } : {}
  },
  component: EndpointsPage,
})

function EndpointsPage() {
  const { t } = useTranslation()
  const endpoints = useChatEndpoints()
  const update = useUpdateChatEndpoint()
  const remove = useDeleteChatEndpoint()
  const rotate = useRotateChatEndpointToken()
  const confirm = useConfirm()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [revealed, setRevealed] = React.useState<ChatEndpoint | null>(null)
  const setEdit = (edit: EndpointsSearch["edit"]) =>
    void navigate({ search: edit ? { edit } : {}, replace: !edit })

  const list = endpoints.data ?? []
  const editing = search.edit === "new" ? null : (list.find((e) => e.id === search.edit) ?? undefined)
  const sheetOpen = search.edit === "new" || !!editing

  const onRotate = (e: ChatEndpoint) =>
    confirm({
      title: t("chat.endpoints.rotateTitle", { name: e.name }),
      description: t("chat.endpoints.rotateDescription"),
      confirmLabel: t("chat.endpoints.rotate"),
      destructive: true,
      onConfirm: async () => setRevealed(await rotate.mutateAsync(e.id)),
    })

  const onDelete = (e: ChatEndpoint) =>
    confirm({
      title: t("chat.endpoints.deleteTitle", { name: e.name }),
      description: t("chat.endpoints.deleteDescription"),
      confirmLabel: t("chat.endpoints.delete"),
      destructive: true,
      confirmText: e.name,
      onConfirm: async () => {
        await remove.mutateAsync(e)
        if (search.edit === e.id) setEdit(undefined)
      },
    })

  const totals = React.useMemo(() => {
    const enabled = list.filter((e) => e.enabled).length
    const requests = list.reduce((n, e) => n + e.invocation_count, 0)
    const last = list.map((e) => toDate(e.last_used_at)?.getTime() ?? 0).reduce((a, b) => Math.max(a, b), 0)
    return { enabled, requests, last: last ? new Date(last).toISOString() : null }
  }, [list])

  const columns = React.useMemo(
    () =>
      createColumns<ChatEndpoint>((c) => [
        c.accessor("enabled", {
          header: t("chat.endpoints.columns.enabled"),
          meta: { label: t("chat.endpoints.columns.enabled") },
          enableSorting: false,
          cell: ({ row }) => (
            <span onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={row.original.enabled}
                aria-label={t("chat.endpoints.toggle", { name: row.original.name })}
                onCheckedChange={(v) => update.mutate({ id: row.original.id, enabled: v, quiet: true })}
              />
            </span>
          ),
        }),
        c.accessor("name", {
          header: t("chat.endpoints.columns.name"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="min-w-0">
              <div className="truncate font-medium">{row.original.name}</div>
              <div className="text-muted-foreground truncate text-xs">{row.original.agent_name}</div>
            </div>
          ),
        }),
        c.accessor("token_prefix", {
          header: t("chat.endpoints.columns.token"),
          meta: { label: t("chat.endpoints.columns.token") },
          enableSorting: false,
          cell: ({ getValue }) => (
            <code className="text-muted-foreground font-mono text-xs">{getValue()}…</code>
          ),
        }),
        c.display({
          id: "behaviour",
          header: t("chat.endpoints.columns.behaviour"),
          meta: { label: t("chat.endpoints.columns.behaviour") },
          cell: ({ row }) => (
            <div className="flex flex-wrap gap-1">
              {row.original.allow_context_continuity && (
                <StatusBadge>
                  <History />
                  {t("chat.endpoints.badges.continuity")}
                </StatusBadge>
              )}
              {row.original.expose_reasoning && (
                <StatusBadge tone="accent">
                  <Brain />
                  {t("chat.endpoints.badges.reasoning")}
                </StatusBadge>
              )}
              {row.original.expose_tool_calling && (
                <StatusBadge tone="info">
                  <Wrench />
                  {t("chat.endpoints.badges.tools")}
                </StatusBadge>
              )}
            </div>
          ),
        }),
        c.accessor("rate_limit_per_minute", {
          header: t("chat.endpoints.columns.rateLimit"),
          meta: { label: t("chat.endpoints.columns.rateLimit"), align: "end" },
          cell: ({ getValue }) => (
            <span className="tabular-nums">{t("chat.endpoints.perMinute", { count: getValue() })}</span>
          ),
        }),
        c.accessor("invocation_count", {
          header: t("chat.endpoints.columns.uses"),
          meta: { label: t("chat.endpoints.columns.uses"), align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor("last_used_at", {
          header: t("chat.endpoints.columns.lastUsed"),
          sortFn: "datetime",
          sortUndefined: "last",
          cell: ({ getValue }) => (
            <RelativeTime value={getValue()} fallback={t("common.never")} className="text-muted-foreground" />
          ),
        }),
      ]),
    [t, update],
  )

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("chat.endpoints.title")}
        description={t("chat.endpoints.description")}
        icon={Webhook}
        actions={
          <Button onClick={() => setEdit("new")} data-testid="endpoint-new">
            <Plus />
            {t("chat.endpoints.new")}
          </Button>
        }
      />
      {list.length > 0 && (
        <div className="mb-6 grid gap-3 sm:grid-cols-3">
          <StatCard
            label={t("chat.endpoints.stats.active")}
            value={`${totals.enabled} / ${list.length}`}
            icon={Webhook}
          />
          <StatCard
            label={t("chat.endpoints.stats.requests")}
            value={formatNumber(totals.requests)}
            icon={Activity}
          />
          <StatCard
            label={t("chat.endpoints.stats.lastUsed")}
            value={<RelativeTime value={totals.last} fallback={t("common.never")} className="text-2xl" />}
            icon={Gauge}
          />
        </div>
      )}
      <DataTable
        label={t("chat.endpoints.title")}
        data={endpoints.data}
        columns={columns}
        getRowId={(e) => String(e.id)}
        loading={endpoints.isPending}
        error={endpoints.error}
        onRetry={() => void endpoints.refetch()}
        searchable={list.length > 5}
        initialSorting={[{ id: "name", desc: false }]}
        onRowClick={(e) => setEdit(e.id)}
        isRowActive={(e) => e.id === search.edit}
        rowActions={(e) => (
          <>
            <DropdownMenuItem onClick={() => setEdit(e.id)}>
              <Pencil />
              {t("chat.endpoints.edit")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => void onRotate(e)}>
              <RefreshCw />
              {t("chat.endpoints.rotate")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => void onDelete(e)}>
              <Trash2 />
              {t("chat.endpoints.delete")}
            </DropdownMenuItem>
          </>
        )}
        emptyState={
          <EmptyState
            variant="plain"
            icon={Webhook}
            title={t("chat.endpoints.emptyTitle")}
            description={t("chat.endpoints.emptyDescription")}
            action={
              <Button size="sm" onClick={() => setEdit("new")}>
                <Plus />
                {t("chat.endpoints.new")}
              </Button>
            }
          />
        }
      />
      <EndpointSheet
        open={sheetOpen}
        endpoint={editing ?? null}
        taken={list.map((e) => e.agent_id)}
        onOpenChange={(o) => !o && setEdit(undefined)}
        onCreated={(e) => {
          setRevealed(e)
          setEdit(undefined)
        }}
        onRotate={onRotate}
        onDelete={onDelete}
      />
      <TokenDialog endpoint={revealed} onClose={() => setRevealed(null)} />
    </PageContainer>
  )
}

// ---------------------------------------------------------------- form

const schema = z.object({
  agent_id: z
    .number({ error: () => i18n.t("validation.required") })
    .int()
    .positive(),
  name: z.string().trim().min(1).max(255),
  enabled: z.boolean(),
  allow_context_continuity: z.boolean(),
  expose_reasoning: z.boolean(),
  expose_tool_calling: z.boolean(),
  rate_limit_per_minute: z.number().int().min(1).max(100_000),
})
type Values = z.infer<typeof schema>

const defaults = (e: ChatEndpoint | null): Values => ({
  agent_id: e?.agent_id ?? (undefined as unknown as number),
  name: e?.name ?? "",
  enabled: e?.enabled ?? true,
  allow_context_continuity: e?.allow_context_continuity ?? true,
  expose_reasoning: e?.expose_reasoning ?? false,
  expose_tool_calling: e?.expose_tool_calling ?? false,
  rate_limit_per_minute: e?.rate_limit_per_minute ?? 60,
})

function EndpointSheet({
  open,
  endpoint,
  taken,
  onOpenChange,
  onCreated,
  onRotate,
  onDelete,
}: {
  open: boolean
  endpoint: ChatEndpoint | null
  taken: number[]
  onOpenChange: (o: boolean) => void
  onCreated: (e: ChatEndpoint) => void
  onRotate: (e: ChatEndpoint) => void
  onDelete: (e: ChatEndpoint) => void
}) {
  const { t } = useTranslation()
  const agents = useAgents()
  const create = useCreateChatEndpoint()
  const update = useUpdateChatEndpoint()
  const form = useForm<Values>({
    resolver: zodResolver(schema) as Resolver<Values>,
    defaultValues: defaults(endpoint),
  })
  // reset when the sheet opens or switches endpoint (not on every list refetch)
  const key = open ? (endpoint?.id ?? "new") : null
  const endpointRef = React.useRef(endpoint)
  endpointRef.current = endpoint
  React.useEffect(() => {
    if (key !== null) form.reset(defaults(endpointRef.current))
  }, [key, form])

  const agentOptions = (agents.data ?? [])
    .filter((a) => a.id === endpoint?.agent_id || !taken.includes(a.id))
    .map((a) => ({ value: a.id, label: a.name, description: a.model_name }))

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      className="sm:max-w-xl"
      title={endpoint ? endpoint.name : t("chat.endpoints.new")}
      description={endpoint ? t("chat.endpoints.editDescription") : t("chat.endpoints.newDescription")}
      submitLabel={endpoint ? t("common.save") : t("chat.endpoints.create")}
      requireDirty={!!endpoint}
      footerStart={
        endpoint ? (
          <Button type="button" variant="destructive" size="sm" onClick={() => onDelete(endpoint)}>
            <Trash2 />
            <span className="max-sm:sr-only">{t("chat.endpoints.delete")}</span>
          </Button>
        ) : undefined
      }
      onSubmit={async (v) => {
        if (endpoint) {
          const { agent_id: _, ...rest } = v
          const e = await update.mutateAsync({ id: endpoint.id, ...rest })
          form.reset(defaults(e))
        } else {
          onCreated(await create.mutateAsync(v))
        }
      }}
    >
      {endpoint ? (
        <KeyValueList
          items={[
            { label: t("chat.endpoints.fields.agent"), value: endpoint.agent_name },
            { label: t("chat.endpoints.columns.uses"), value: formatNumber(endpoint.invocation_count) },
            {
              label: t("chat.endpoints.columns.lastUsed"),
              value: <RelativeTime value={endpoint.last_used_at} fallback={t("common.never")} />,
            },
            { label: t("chat.endpoints.columns.token"), value: `${endpoint.token_prefix}…`, mono: true },
          ]}
        />
      ) : (
        <SelectField
          control={form.control}
          name="agent_id"
          label={t("chat.endpoints.fields.agent")}
          description={t("chat.endpoints.fields.agentHint")}
          required
          placeholder={t("chat.endpoints.fields.agentPlaceholder")}
          options={agentOptions}
        />
      )}
      <TextField control={form.control} name="name" label={t("chat.endpoints.fields.name")} required />
      <SwitchField
        control={form.control}
        name="enabled"
        variant="card"
        label={t("chat.endpoints.fields.enabled")}
        description={t("chat.endpoints.fields.enabledHint")}
      />
      <SwitchField
        control={form.control}
        name="allow_context_continuity"
        variant="card"
        icon={History}
        label={t("chat.endpoints.fields.continuity")}
        description={t("chat.endpoints.fields.continuityHint")}
      />
      <SwitchField
        control={form.control}
        name="expose_reasoning"
        variant="card"
        icon={Brain}
        label={t("chat.endpoints.fields.reasoning")}
        description={t("chat.endpoints.fields.reasoningHint")}
      />
      <SwitchField
        control={form.control}
        name="expose_tool_calling"
        variant="card"
        icon={Wrench}
        label={t("chat.endpoints.fields.tools")}
        description={t("chat.endpoints.fields.toolsHint")}
      />
      <NumberField
        control={form.control}
        name="rate_limit_per_minute"
        label={t("chat.endpoints.fields.rateLimit")}
        description={t("chat.endpoints.fields.rateLimitHint")}
        min={1}
        max={100000}
        unit={t("chat.endpoints.fields.perMinuteUnit")}
      />
      {endpoint && (
        <>
          <Separator />
          <EndpointApi endpoint={endpoint} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => onRotate(endpoint)}
          >
            <RefreshCw />
            {t("chat.endpoints.rotate")}
          </Button>
        </>
      )}
    </FormSheet>
  )
}

function EndpointApi({ endpoint, token }: { endpoint: ChatEndpoint; token?: string }) {
  const { t } = useTranslation()
  const url = endpointUrl(endpoint.id)
  const opts = { continuity: endpoint.allow_context_continuity, token }
  return (
    <div className="min-w-0 space-y-3">
      <div className="space-y-1.5">
        <div className="text-[0.8125rem] font-medium">{t("chat.endpoints.api.url")}</div>
        <div className="bg-muted/50 flex items-center gap-1 rounded-lg border p-1 pl-3">
          <code className="min-w-0 flex-1 truncate font-mono text-[0.8rem]">{url}</code>
          <CopyButton value={url} />
        </div>
      </div>
      <Tabs defaultValue="json">
        <TabsList>
          <TabsTrigger value="json">{t("chat.endpoints.api.json")}</TabsTrigger>
          <TabsTrigger value="sse">{t("chat.endpoints.api.sse")}</TabsTrigger>
        </TabsList>
        <TabsContent value="json" className="min-w-0 space-y-2 pt-2">
          <CodeBlock
            language="bash"
            wrap
            className="[&_pre]:[overflow-wrap:anywhere]"
            code={endpointCurl(url, { ...opts, stream: false })}
          />
          <p className="text-muted-foreground text-xs">{t("chat.endpoints.api.jsonHint")}</p>
        </TabsContent>
        <TabsContent value="sse" className="min-w-0 space-y-2 pt-2">
          <CodeBlock
            language="bash"
            wrap
            className="[&_pre]:[overflow-wrap:anywhere]"
            code={endpointCurl(url, { ...opts, stream: true })}
          />
          <p className="text-muted-foreground text-xs">
            {endpoint.expose_reasoning
              ? t("chat.endpoints.api.sseHintFull")
              : t("chat.endpoints.api.sseHintStatus")}
          </p>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function TokenDialog({ endpoint, onClose }: { endpoint: ChatEndpoint | null; onClose: () => void }) {
  const { t } = useTranslation()
  const [shown, setShown] = React.useState(endpoint)
  React.useEffect(() => {
    if (endpoint) setShown(endpoint)
  }, [endpoint])
  const e = endpoint ?? shown
  return (
    <Dialog open={!!endpoint} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        {e?.token && (
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="bg-success/15 text-success flex size-6 items-center justify-center rounded-full">
                  <Check className="size-3.5" />
                </span>
                {t("chat.endpoints.tokenTitle", { name: e.name })}
              </DialogTitle>
              <DialogDescription>{t("chat.endpoints.tokenDescription")}</DialogDescription>
            </DialogHeader>
            <SecretReveal secret={e.token} />
            <EndpointApi endpoint={e} token={e.token} />
            <DialogFooter>
              <Button onClick={onClose} data-testid="token-done">
                {t("chat.endpoints.done")}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
